import { IFinanceRoundDataModel } from '../../../../data-models/finance-round.data-model';
import { MetricsTransactionDataModel } from '../../../../schemas/MetricsTransaction.schema';
import { FMT } from '../../../../util/formatter-service';
import { amountsMatch } from '../../../../util/math-utils';
import { getMoic, IEquityLot, lotKeyOf, ProceedsTransTypes, toDate } from './equity-holdings-model';
import { getSecurityClass } from './security-class-model';

/**
 * The lifecycle "story" a Security History row can tell, and the conversion links between rows.
 *
 * Everything here is derived from `Investran` transactions, which reach the frontend on
 * `companyMetricsAtomJ`. `securityReport` aggregates them away, which is why the card could not show
 * individual sales before.
 *
 * Only **confirmed** transactions count. A pending entry is provisional — the app marks pending impact
 * with a warning icon elsewhere rather than applying it — and an unconfirmed sale must not appear in a
 * story as though it happened.
 *
 * Nothing here decides whether a lot is listed or held. That separation is deliberate: an earlier
 * attempt to read conversions out of transactions changed which rows existed and silently emptied
 * Security Overview. These derivations are presentational only, so a wrong guess shows a wrong chip
 * rather than losing a holding.
 */

const RestructureOutTransType = 'Investment - Restructure Out';
const RestructureInTransType = 'Investment - Restructure In';

function isConfirmed(transaction: MetricsTransactionDataModel): boolean {
  return transaction.status === 'Confirmed';
}

/**
 * The round terms the story chips print: the cap a SAFE was bought under, the post-money a conversion
 * priced at, and whether the round qualified for QSBS. A transaction names the round it belongs to, so
 * the join is `transaction.roundId -> round.id`.
 */
export interface IRoundTerms {
  valuationCap: number | null;
  postMoneyValuation: number | null;
  qualifiesForQsbs: boolean;
}

export function toRoundTerms(round: IFinanceRoundDataModel): IRoundTerms {
  return {
    // The hand-entered term only. The backend reconciles three further sources for the Round Tracker
    // — Carta, Aumni and pending transactions, each on its own scale — and none of them reaches this
    // response, so a cap recorded elsewhere prints nothing rather than a figure that may be wrong.
    valuationCap: round.manualValuationCap ?? null,
    postMoneyValuation: round.postMoneyValuation,
    qualifiesForQsbs: round.initialQSBSQualification,
  };
}

export interface ISaleEvent {
  date: Date | null;
  /** Shares disposed of. Zero where the ledger records an amount but no quantity. */
  shares: number;
  proceeds: number;
  fundId: number | null;
  /** A sale the ledger labelled as one, or capital coming back by some other route. */
  kind: 'sale' | 'distribution';
}

/**
 * Individual disposals, oldest first — the events behind the design's "Sold 90,000 sh · $1.1M" chip.
 *
 * The rule matches the number the Proceeds column shows. `securityReport.realizedValue` is the sum of
 * `distributions` across a lot's transactions **whatever their type** (see `calculateRealizedValue`),
 * so every transaction carrying one is an event in the story. Selecting on the sale-like transaction
 * types alone left rows reporting proceeds with nothing in their story to account for them — capital
 * that came back as, say, capitalized interest carries a distribution but is not typed as a sale.
 *
 * The typed sales are still included even when their distribution is empty: the ledger has called them
 * disposals, and a sale with no cash attached to it is still something that happened.
 */
export function getSaleEvents(transactions: MetricsTransactionDataModel[] | undefined): ISaleEvent[] {
  if (transactions === undefined) {
    return [];
  }

  return transactions
    .filter((t) => isConfirmed(t) && (isTypedSale(t) || Math.abs(t.distributions ?? 0) > 0))
    .map<ISaleEvent>((t) => ({
      date: toDate(t.transactionDate),
      // Disposals are negative quantities in the ledger; a sale of 90,000 shares reads as -90,000.
      shares: Math.abs(t.quantity ?? 0),
      // Distributions only, never `amount`. The Proceeds column counts distributions, so falling back
      // to a transaction's amount reports capital movements as realized value and the story stops
      // reconciling with the row it belongs to.
      proceeds: Math.abs(t.distributions ?? 0),
      fundId: t.fundId ?? null,
      kind: isTypedSale(t) ? 'sale' : 'distribution',
    }))
    .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
}

function isTypedSale(transaction: MetricsTransactionDataModel): boolean {
  return transaction.transType !== undefined && ProceedsTransTypes.has(transaction.transType);
}

/**
 * Conversion links between lots, as `lotId -> links`. Both sides of a conversion share a pair id, so
 * the grid can chip them, colour them and highlight them together.
 *
 * A conversion is a restructure-out of one lot and a restructure-in of another on the same date within
 * the same fund — capital does not cross funds. Where a date and fund have anything other than exactly
 * one out and one in, the pairing is ambiguous and is skipped: no chip is better than a wrong link.
 *
 * A lot carries a **list** of links, not one: the middle of a chain — a SAFE that became a Series A-1
 * that itself converted into a Series E — is created by one conversion and consumed by another, and
 * keeping only one of the two would leave the chain unwalkable from either end.
 */
export interface IConversionLink {
  /** Shared by both sides, for colouring and highlighting them together. */
  pairId: string;
  /** The lot on the other side, so a chip can name it as the design does. */
  counterpartLotId: string;
  /** `to` converted into the counterpart; `from` was created by it. */
  direction: 'to' | 'from';
  /** When the conversion happened, so the chain can be told in date order. */
  date: Date | null;
  /** Shares the conversion issued, off the restructure-in leg. Null where the ledger records none. */
  shares: number | null;
  /** What those shares were priced at. */
  pricePerShare: number | null;
  /** The round the new security belongs to, for the post-money the design prints beside it. */
  roundId: number | null;
}

export function getConversionLinks(
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>
): Map<string, IConversionLink[]> {
  interface ILeg {
    lotId: string;
    amount: number;
    shares: number | null;
    pricePerShare: number | null;
    roundId: number | null;
  }
  const outs = new Map<string, ILeg[]>();
  const ins = new Map<string, ILeg[]>();
  const dates = new Map<string, Date>();

  for (const [lotId, transactions] of transactionsByLot) {
    for (const transaction of transactions) {
      if (!isConfirmed(transaction)) {
        continue;
      }
      const date = toDate(transaction.transactionDate);
      if (date === null) {
        continue;
      }
      const bucket = `${date.getTime()}:${transaction.fundId ?? 'no-fund'}`;
      dates.set(bucket, date);
      const shares = sharesOf(transaction);
      const leg = {
        lotId,
        amount: Math.abs(transaction.amount ?? 0),
        shares,
        pricePerShare: pricePerShareOf(transaction, shares),
        roundId: transaction.roundId ?? null,
      };
      if (transaction.transType === RestructureOutTransType) {
        outs.set(bucket, [...(outs.get(bucket) ?? []), leg]);
      } else if (transaction.transType === RestructureInTransType) {
        ins.set(bucket, [...(ins.get(bucket) ?? []), leg]);
      }
    }
  }

  const links = new Map<string, IConversionLink[]>();
  const add = (lotId: string, link: IConversionLink) => links.set(lotId, [...(links.get(lotId) ?? []), link]);

  for (const [bucket, outLegs] of outs) {
    const inLegs = [...(ins.get(bucket) ?? [])];

    for (const outLeg of outLegs) {
      // Both legs come from one source transaction, emitted as -(principal + interest) and
      // +(principal + interest) — see the SAFE Conversion case in metrics-engine.service.ts. The
      // matching amount is what identifies a counterpart, and it separates several conversions
      // sharing a date and fund, which counting alone could not. A zero amount identifies nothing, so
      // a leg without one never pairs — otherwise every zero leg would match every other.
      const candidates = inLegs.filter(
        (inLeg) =>
          inLeg.lotId !== outLeg.lotId && inLeg.amount > 0 && amountsMatch(inLeg.amount, outLeg.amount)
      );
      // The amount has to identify *both* sides. Two outs of the same size against one in is
      // ambiguous, and picking either would assert a link that may not exist.
      const rivals = outLegs.filter(
        (other) =>
          other.lotId !== outLeg.lotId && other.amount > 0 && amountsMatch(other.amount, outLeg.amount)
      );
      if (candidates.length !== 1 || rivals.length > 0) {
        continue;
      }
      const inLeg = candidates[0];
      inLegs.splice(inLegs.indexOf(inLeg), 1);

      const pairId = `pair:${bucket}:${outLeg.lotId}`;
      const date = dates.get(bucket) ?? null;
      // Both sides describe one event, and it is the *in* leg that issued the new shares — the out leg
      // only records the capital leaving. So both links carry the in leg's figures.
      const issued = { shares: inLeg.shares, pricePerShare: inLeg.pricePerShare, roundId: inLeg.roundId };
      add(outLeg.lotId, { pairId, counterpartLotId: inLeg.lotId, direction: 'to', date, ...issued });
      add(inLeg.lotId, { pairId, counterpartLotId: outLeg.lotId, direction: 'from', date, ...issued });
    }
  }
  return links;
}

/** Quantities are signed in the ledger; a count is a count. Zero means "not recorded". */
function sharesOf(transaction: MetricsTransactionDataModel): number | null {
  const quantity = Math.abs(transaction.quantity ?? 0);
  return quantity > 0 ? quantity : null;
}

/**
 * A price below half a cent is reported as absent rather than shown. The currency formatter renders
 * two decimals, so anything smaller reads as "@ $0.00" — a claim that the security was reissued for
 * nothing. Restructure legs often carry a nominal price or amount, and no price is the honest reading.
 */
const SmallestShownPrice = 0.005;

/** The ledger's own price where it carries one, otherwise what the leg's amount implies. */
function pricePerShareOf(transaction: MetricsTransactionDataModel, shares: number | null): number | null {
  const pps = transaction.pps;
  if (pps != null && pps >= SmallestShownPrice) {
    return pps;
  }
  const amount = Math.abs(transaction.amount ?? 0);
  const implied = shares !== null && amount > 0 ? amount / shares : null;
  return implied !== null && implied >= SmallestShownPrice ? implied : null;
}

/**
 * Restructures recorded **within one lot** — both legs on the same `restructureId`, keyed by lot.
 *
 * One of two shapes the ledger uses, decided by the `Create_Captables_From_Deal_Docs` client config:
 * enabled, the engine assigns one `restructureId` before emitting both legs, so the security
 * transforms in place; disabled, the in leg gets a fresh `restructureId`, producing the two-row shape
 * the design draws. The in-place shape is the common one.
 *
 * An in-place restructure has no counterpart row to link to or highlight, but it is still an event in
 * the security's history and the story has to tell it — otherwise a position that demonstrably
 * restructured shows its purchase and then jumps straight to today.
 */
export interface IInPlaceRestructure {
  date: Date | null;
  /** Shares the restructure issued, off the in leg. */
  shares: number | null;
  pricePerShare: number | null;
  /** The round the in leg belongs to, for the post-money. */
  roundId: number | null;
}

/**
 * One event's in legs, folded into the figures the chip prints. Shares sum. The price is the legs'
 * own where they agree, otherwise the blend — total money over total shares — which is the true
 * average the event issued at. The round is taken only when every leg names the same one: unlike the
 * shares, a round is an identity, and claiming either of two is a guess.
 */
function mergeInLegs(
  legs: MetricsTransactionDataModel[]
): Pick<IInPlaceRestructure, 'shares' | 'pricePerShare' | 'roundId'> {
  if (legs.length === 1) {
    const shares = sharesOf(legs[0]);
    return { shares, pricePerShare: pricePerShareOf(legs[0], shares), roundId: legs[0].roundId ?? null };
  }

  const totalShares = legs.reduce((total, leg) => total + (sharesOf(leg) ?? 0), 0);
  const shares = totalShares > 0 ? totalShares : null;

  const statedPrices = new Set(
    legs.map((leg) => leg.pps).filter((pps): pps is number => pps != null && pps >= SmallestShownPrice)
  );
  const totalAmount = legs.reduce((total, leg) => total + Math.abs(leg.amount ?? 0), 0);
  const blended = shares !== null && totalAmount > 0 ? totalAmount / shares : null;
  const pricePerShare =
    statedPrices.size === 1
      ? [...statedPrices][0]
      : blended !== null && blended >= SmallestShownPrice
        ? blended
        : null;

  const roundIds = new Set(legs.map((leg) => leg.roundId ?? null));
  return { shares, pricePerShare, roundId: roundIds.size === 1 ? ([...roundIds][0] ?? null) : null };
}

export function getInPlaceRestructures(
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>
): Map<string, IInPlaceRestructure[]> {
  const inPlace = new Map<string, IInPlaceRestructure[]>();

  for (const [lotId, transactions] of transactionsByLot) {
    const confirmed = transactions.filter(isConfirmed);
    // The legs must belong to the same event, mirroring `getConversionLinks`' bucketing. Merely having
    // an Out somewhere and an In somewhere is a different, multi-hop history: a lot created by
    // converting in during 2018 and itself converting out in 2021 has two cross-lot events, not one
    // in-place transformation, and must not be flattened into a single chip.
    const buckets = confirmed.reduce((acc, transaction) => {
      if (
        transaction.transType !== RestructureOutTransType &&
        transaction.transType !== RestructureInTransType
      ) {
        return acc;
      }
      const date = toDate(transaction.transactionDate);
      if (date === null) {
        return acc;
      }
      const key = `${date.getTime()}:${transaction.fundId ?? 'no-fund'}`;
      const seen = acc.get(key) ?? { out: false, ins: [] as MetricsTransactionDataModel[], date };
      if (transaction.transType === RestructureOutTransType) {
        seen.out = true;
      } else {
        seen.ins.push(transaction);
      }
      return acc.set(key, seen);
    }, new Map<string, { out: boolean; ins: MetricsTransactionDataModel[]; date: Date }>());

    // The in legs are what issued the new shares, so they carry the figures the chip shows. All of
    // them: an event recorded as several in legs — a split issuance — issued every one, so the shares
    // are additive facts and get summed, unlike `getConversionLinks`' pairing, where an ambiguous
    // match is a wrong assertion and is skipped.
    const events = [...buckets.values()]
      .filter((bucket) => bucket.out && bucket.ins.length > 0)
      .map((bucket) => ({ date: bucket.date, ...mergeInLegs(bucket.ins) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (events.length > 0) {
      inPlace.set(lotId, events);
    }
  }
  return inPlace;
}

export interface IStoryChip {
  /** Stable identity for the chip's position in the sequence — a story can repeat "Sold". */
  id: string;
  /** Uppercase label, e.g. "Purchased". */
  title: string;
  /** The headline figures. */
  value: string;
  /** The muted line beneath. */
  detail: string;
  /** Tints the end chip by outcome. */
  tone: 'neutral' | 'gain' | 'loss';
}

export interface IBuildChainStoryParams {
  /** The row that was selected; the chain is walked out from it in both directions. */
  lotId: string;
  lotsById: Map<string, IEquityLot>;
  transactions: (lot: IEquityLot) => MetricsTransactionDataModel[] | undefined;
  links: Map<string, IConversionLink[]>;
  fundName: (fundId: number | null) => string;
  /** Round terms by round id, for the cap, the post-money and the QSBS clock the design prints. */
  roundTerms: Map<number, IRoundTerms>;
  /** Restructures a lot recorded within itself, which have no counterpart row to link to. */
  inPlaceRestructures: Map<string, IInPlaceRestructure[]>;
}

/**
 * The chip sequence for a whole restructure chain, in date order: what was bought, every conversion
 * along the way, each disposal, and where the capital ended up.
 *
 * It has to be the chain and not the selected lot alone: the conversion that created the lot a
 * reader clicked, and everything the capital did before it, live on other rows. Built from one lot,
 * a converted position shows its purchase and then jumps straight to today.
 *
 * The chain's shape decides which chips a lot contributes:
 *
 * - only a lot **nothing converted into** gets a "Purchased" chip; every other lot in the chain was
 *   created by a conversion, and the conversion chip already says where its capital came from;
 * - only a lot that **did not convert onward** gets the closing chip, since a converted lot's outcome
 *   is the conversion itself.
 *
 * The as-of date needs no handling here: `selectTransactionsAsOf` cuts the transactions the links
 * are built from, so an event past the date leaves no link and the chain simply stops there.
 */
export function buildChainStory({
  lotId,
  lotsById,
  transactions,
  links,
  fundName,
  roundTerms,
  inPlaceRestructures,
}: IBuildChainStoryParams): IStoryChip[] {
  const chain = collectChain(lotId, links, lotsById);
  if (chain.length === 0) {
    return [];
  }

  const termsOf = (roundId: number | null | undefined) =>
    roundId == null ? undefined : roundTerms.get(roundId);
  // The chain bought in once, and the QSBS clock runs from that purchase through every conversion
  // after it — which is what the design prints on the closing chip.
  const origin = chain.find((lot) => !(links.get(lot.id) ?? []).some((link) => link.direction === 'from'));
  const originTerms = origin === undefined ? undefined : termsOf(roundIdOf(transactions(origin)));
  const qsbsClockSince =
    originTerms?.qualifiesForQsbs === true && origin?.investmentDate != null
      ? formatDate(origin.investmentDate)
      : '';

  interface IDatedChip {
    time: number;
    chip: Omit<IStoryChip, 'id'>;
  }
  const dated: IDatedChip[] = [];
  const closing: IDatedChip[] = [];

  for (const lot of chain) {
    const lotLinks = links.get(lot.id) ?? [];
    const createdByConversion = lotLinks.some((link) => link.direction === 'from');
    const convertedOnward = lotLinks.filter((link) => link.direction === 'to');

    if (!createdByConversion) {
      dated.push({
        time: timeOf(lot.investmentDate),
        chip: purchaseChip(lot, fundName, termsOf(roundIdOf(transactions(lot)))),
      });
    }

    for (const link of convertedOnward) {
      dated.push({
        time: timeOf(link.date),
        chip: conversionChip(lot, lotsById.get(link.counterpartLotId), link, termsOf(link.roundId)),
      });
    }

    // A restructure recorded within the lot has no counterpart to name, but it happened and the story
    // is wrong without it. This is the shape most ledgers use.
    for (const event of inPlaceRestructures.get(lot.id) ?? []) {
      dated.push({
        time: timeOf(event.date),
        chip: restructureChip(lot, event, termsOf(event.roundId)),
      });
    }

    const sales = getSaleEvents(transactions(lot));
    // Two sales are shown individually; beyond that they are grouped, as the prototype does, so the
    // banner cannot grow without bound.
    if (sales.length > 0 && sales.length <= 2) {
      sales.forEach((sale) => dated.push({ time: timeOf(sale.date), chip: saleChip(sale, fundName) }));
    } else if (sales.length > 2) {
      dated.push({ time: timeOf(sales[0].date), chip: groupedSaleChip(sales) });
    }

    if (convertedOnward.length === 0) {
      closing.push({ time: Number.MAX_SAFE_INTEGER, chip: outcomeChip(lot, sales, qsbsClockSince) });
    }
  }

  // A stable sort keeps same-day events in the order they were pushed — purchase, conversion, sale —
  // which is the order they must have happened in.
  const chips = [...dated.sort((a, b) => a.time - b.time), ...closing].map((entry) => entry.chip);

  // Position is part of the identity: a chain legitimately repeats "Sold".
  return chips.map((chip, index) => ({ ...chip, id: `${lotId}:${index}:${chip.title}` }));
}

/**
 * Every lot reachable from the selected one by conversion links, oldest first.
 *
 * Walked in both directions and bounded by the visited set, so a ledger that records a cycle cannot
 * hang the banner.
 */
function collectChain(
  lotId: string,
  links: Map<string, IConversionLink[]>,
  lotsById: Map<string, IEquityLot>
): IEquityLot[] {
  const visited = new Set<string>();
  const pending = [lotId];

  while (pending.length > 0) {
    const current = pending.shift();
    if (current === undefined || visited.has(current)) {
      continue;
    }
    visited.add(current);
    for (const link of links.get(current) ?? []) {
      pending.push(link.counterpartLotId);
    }
  }

  return [...visited]
    .map((id) => lotsById.get(id))
    .filter((lot): lot is IEquityLot => lot != null)
    .sort((a, b) => timeOf(a.investmentDate) - timeOf(b.investmentDate));
}

function timeOf(date: Date | null | undefined): number {
  return date == null ? Number.MAX_SAFE_INTEGER : date.getTime();
}

/**
 * The opening chip is titled by what was bought — "SAFE", "Series A Preferred" — with the cheque and,
 * for a capped instrument, the cap it was written under. A class the parser could not place falls back
 * to the plain verb, since a title of "1605.9.9." tells a reader nothing.
 */
function purchaseChip(
  lot: IEquityLot,
  fundName: (fundId: number | null) => string,
  terms: IRoundTerms | undefined
): Omit<IStoryChip, 'id'> {
  const securityClass = getSecurityClass(lot.securityClass);
  const cap = terms?.valuationCap == null ? '' : ` @ ${FMT.format('currencyShort', terms.valuationCap)} cap`;

  return {
    title: securityClass.kind === 'other' ? 'Purchased' : securityClass.label,
    value: `${FMT.format('currencyShort', lot.invested)}${cap}`,
    // The security is named on the row this was opened from, and the conversion chip names what it
    // became, so the detail line carries only what neither of those says.
    detail: joinParts([formatDate(lot.investmentDate), fundName(lot.fundId)]),
    tone: 'neutral',
  };
}

/**
 * What the conversion issued — "80,533 sh @ $6.21" — over the date, the post-money it priced at and
 * the security it became.
 *
 * Where the ledger records neither a quantity nor a price on the in leg, the chip names the two
 * classes instead ("SAFE → A-1"): a conversion with nothing to say about its size still has to say
 * that it happened.
 */
function conversionChip(
  lot: IEquityLot,
  counterpart: IEquityLot | undefined,
  link: IConversionLink,
  terms: IRoundTerms | undefined
): Omit<IStoryChip, 'id'> {
  const from = getSecurityClass(lot.securityClass).shortLabel;
  const to = counterpart === undefined ? '' : getSecurityClass(counterpart.securityClass).shortLabel;
  const issued = joinParts(
    [
      link.shares === null ? '' : `${FMT.format('integer', link.shares)} sh`,
      link.pricePerShare === null ? '' : `@ ${FMT.format('currency', link.pricePerShare)}`,
    ],
    ' '
  );
  const post =
    terms?.postMoneyValuation == null ? '' : `${FMT.format('currencyShort', terms.postMoneyValuation)} post`;

  return {
    title: 'Converted',
    value: issued === '' ? (to === '' ? from : `${from} → ${to}`) : issued,
    detail: joinParts([formatDate(link.date), post, counterpart?.securityName ?? '']),
    tone: 'neutral',
  };
}

/**
 * A restructure the lot recorded within itself: what it reissued and when. There is no counterpart
 * security to name, so the chip leads with the shares and falls back to the lot's own class.
 */
function restructureChip(
  lot: IEquityLot,
  event: IInPlaceRestructure,
  terms: IRoundTerms | undefined
): Omit<IStoryChip, 'id'> {
  const issued = joinParts(
    [
      event.shares === null ? '' : `${FMT.format('integer', event.shares)} sh`,
      event.pricePerShare === null ? '' : `@ ${FMT.format('currency', event.pricePerShare)}`,
    ],
    ' '
  );
  const post =
    terms?.postMoneyValuation == null ? '' : `${FMT.format('currencyShort', terms.postMoneyValuation)} post`;

  return {
    title: 'Restructured',
    value: issued === '' ? getSecurityClass(lot.securityClass).shortLabel : issued,
    detail: joinParts([formatDate(event.date), post]),
    tone: 'neutral',
  };
}

function saleChip(sale: ISaleEvent, fundName: (fundId: number | null) => string): Omit<IStoryChip, 'id'> {
  return {
    // Capital returning by another route is not a sale, and saying so would misread the story.
    title: sale.kind === 'sale' ? 'Sold' : 'Distribution',
    value: disposalValue(sale.shares, sale.proceeds),
    detail: [formatDate(sale.date), fundName(sale.fundId)].filter((part) => part !== '').join(' · '),
    tone: 'neutral',
  };
}

/**
 * Shares and money, dropping whichever the ledger did not record. A disposal with no distribution
 * against it still happened, and "90,000 sh · $0" reads as a sale that returned nothing rather than
 * one whose proceeds are booked elsewhere.
 */
function disposalValue(shares: number, proceeds: number): string {
  const parts = joinParts([
    shares > 0 ? `${FMT.format('integer', shares)} sh` : '',
    proceeds > 0 ? FMT.format('currencyShort', proceeds) : '',
  ]);
  return parts === '' ? FMT.format('currencyShort', 0) : parts;
}

function groupedSaleChip(sales: ISaleEvent[]): Omit<IStoryChip, 'id'> {
  const shares = sales.reduce((total, sale) => total + sale.shares, 0);
  const proceeds = sales.reduce((total, sale) => total + sale.proceeds, 0);
  const first = formatDate(sales[0].date);
  const last = formatDate(sales[sales.length - 1].date);
  const allSales = sales.every((sale) => sale.kind === 'sale');

  return {
    title: allSales ? `Sold (${sales.length} sales)` : `Proceeds (${sales.length} events)`,
    value: disposalValue(shares, proceeds),
    detail: first === last ? first : `${first} → ${last}`,
    tone: 'neutral',
  };
}

function outcomeChip(lot: IEquityLot, sales: ISaleEvent[], qsbsClockSince: string): Omit<IStoryChip, 'id'> {
  // A lot with nothing invested has no multiple, and the formatter renders that as nothing — so the
  // separator has to go with it rather than leaving the chip ending in a dangling "·".
  const moic = FMT.format('multiplier', lot.moic);
  const qsbs = qsbsClockSince === '' ? '' : `QSBS clock since ${qsbsClockSince}`;

  if (lot.lifecycle === 'writtenOff') {
    return {
      title: 'Written Off',
      value: joinParts([FMT.format('currencyShort', 0), moic]),
      detail: joinParts([formatDate(lot.investmentDate), qsbs]),
      tone: 'loss',
    };
  }
  if (lot.lifecycle === 'fullyRealized') {
    const exited = sales.length === 0 ? '' : formatDate(sales[sales.length - 1].date);
    return {
      title: 'Fully Realized',
      value: joinParts([FMT.format('currencyShort', lot.proceeds), moic]),
      detail: joinParts([exited === '' ? '' : `exited ${exited}`, qsbs]),
      tone: 'gain',
    };
  }
  // A lot the ledger calls converted with no link to show for it: the counterpart row is not in this
  // response, so the chain ends here and says only that the capital moved on.
  if (lot.lifecycle === 'converted') {
    return {
      title: 'Converted',
      value: `${FMT.format('currencyShort', lot.invested)} carried`,
      detail: 'into another security',
      tone: 'neutral',
    };
  }

  const gained = getMoic(lot.fmv, lot.proceeds, lot.invested);
  const owned = lot.ownershipPct === null ? '' : `${FMT.format('percent', lot.ownershipPct)} owned`;
  return {
    title: 'Today',
    value: joinParts([FMT.format('currencyShort', lot.fmv), moic]),
    // The QSBS clock is what the design prints here; ownership stands in for a round that does not
    // qualify, which would otherwise leave the chip's last line empty.
    detail: qsbs === '' ? owned : qsbs,
    tone: gained !== undefined && gained < 1 ? 'loss' : 'gain',
  };
}

/** Drops the parts that came out empty, so a missing figure cannot leave a dangling separator. */
function joinParts(parts: string[], separator = ' · '): string {
  return parts.filter((part) => part !== '').join(separator);
}

/** A lot's round, from the first confirmed transaction that names one. */
function roundIdOf(transactions: MetricsTransactionDataModel[] | undefined): number | null {
  return transactions?.find((t) => isConfirmed(t) && t.roundId != null)?.roundId ?? null;
}

function formatDate(date: Date | null): string {
  return date === null ? '' : FMT.format('date', date);
}

/**
 * The capital thread each lot belongs to, as `lotId -> thread id`.
 *
 * A thread is everything the same capital passed through, joined transitively by two relations:
 *
 * 1. **A cross-lot conversion** — the design's relation, where a ledger splits a restructure's two
 *    legs across `restructureId`s. A chain (SAFE → A-1 → E) is one thread end to end.
 * 2. **One security across several funds** — the same `restructureId` as a lot per fund, i.e. one
 *    position held in pieces.
 *
 * One thread gets **one colour**, however many hops it has. Colouring each relation separately reads
 * as noise: a lot in the middle of a thread — converted from a SAFE and held in two funds — paints
 * its neighbours in two different colours, the opposite of "keep the colours the same for a single
 * restructure ID".
 *
 * Sharing a security *class* deliberately does **not** join a thread. Two lots under one band can be
 * different securities bought years apart in different funds, and tinting them together asserts a link
 * that is not there: a highlight that is reachable but wrong is worse than one that stays silent.
 *
 * Every lot gets a thread id — a singleton thread names the colour of its own chips — but only threads
 * holding more than one row ever highlight; see `getHighlightedRows`.
 */
export function getCapitalThreads(
  lots: IEquityLot[],
  links: Map<string, IConversionLink[]>
): Map<string, string> {
  const parents = new Map<string, string>();
  const rootOf = (id: string): string => {
    let current = id;
    while ((parents.get(current) ?? current) !== current) {
      current = parents.get(current) ?? current;
    }
    return current;
  };
  const join = (a: string, b: string) => {
    const [rootA, rootB] = [rootOf(a), rootOf(b)];
    if (rootA !== rootB) {
      // The smaller root wins, so a thread's id is stable whatever order its edges arrive in.
      parents.set(rootA < rootB ? rootB : rootA, rootA < rootB ? rootA : rootB);
    }
  };

  for (const [lotId, lotLinks] of links) {
    for (const link of lotLinks) {
      join(lotId, link.counterpartLotId);
    }
  }

  const byRestructure = new Map<string, string>();
  for (const lot of lots) {
    if (lot.restructureId === '') {
      continue;
    }
    const first = byRestructure.get(lot.restructureId);
    if (first === undefined) {
      byRestructure.set(lot.restructureId, lot.id);
    } else {
      join(first, lot.id);
    }
  }

  return lots.reduce((acc, lot) => acc.set(lot.id, `thread:${rootOf(lot.id)}`), new Map<string, string>());
}

/**
 * The rows tinted while `lotId` is hovered or selected: the lot and everything **directly** tied to
 * it — its conversion counterparts and its own security's other fund slices.
 *
 * Deliberately not the whole thread. Hovering a SAFE that fed one A-1 should light that pair, not
 * four rows because the A-1 is also held elsewhere — the wash answers "what is this row linked to",
 * and the chips' shared thread colour is what says the wider chain is one capital. The wash itself is
 * a single fixed colour (see the grid's CSS): the identity lives in the chips, so the highlight does
 * not need a hue of its own, and a hue that varied by thread read as inconsistency.
 *
 * A lot with nothing directly tied to it tints nothing: there is no relation to show.
 */
export function getLinkedRows(
  lotId: string | null,
  lots: IEquityLot[],
  links: Map<string, IConversionLink[]>
): Set<string> {
  if (lotId === null) {
    return new Set();
  }
  const linked = new Set<string>();
  for (const link of links.get(lotId) ?? []) {
    linked.add(link.counterpartLotId);
  }
  const restructureId = lots.find((lot) => lot.id === lotId)?.restructureId ?? '';
  if (restructureId !== '') {
    for (const lot of lots) {
      if (lot.restructureId === restructureId && lot.id !== lotId) {
        linked.add(lot.id);
      }
    }
  }
  if (linked.size === 0) {
    return new Set();
  }
  linked.add(lotId);
  return linked;
}

/** The transactions of a lot, keyed the way `groupTransactionsByLot` keys them. */
export function transactionsOfLot(
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>
): (lot: IEquityLot) => MetricsTransactionDataModel[] | undefined {
  return (lot) => transactionsByLot.get(lotKeyOf(lot.restructureId, lot.fundId));
}
